import {
  faCalendar,
  faCalendarHeart,
  faLocationDot,
  faMagnifyingGlassLocation,
  faTrashCan,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { parseAsString, useQueryState } from 'nuqs';
import { Fragment, memo, useEffect, useState } from 'react';
import { useTripContext } from '@/contexts/TripContext';
import { TripPhotosCorner } from '@/design-system/composite/TripPhotosCorner';
import { Button } from '@/design-system/primitives/Button';
import { CategoryPin } from '@/design-system/primitives/CategoryPin';
import { IconButton } from '@/design-system/primitives/IconButton';
import { InlineEmptyState } from '@/design-system/primitives/InlineEmptyState';
import { SectionEyebrow } from '@/design-system/primitives/SectionEyebrow';
import type { Trip, TripPlace } from '@/types/trip';
import { formatAddress } from '@/utils/address';
import { categoryDefinitions } from '@/utils/categories';
import {
  dateForDay,
  formatDateRange,
  formatDurationMinutes,
  formatScheduledTime,
  lodgingTimelineTime,
  tripDayCount,
} from '@/utils/schedule';
import { twx } from '@/utils/tailwind';

export interface ScoutedListProps {
  trip: Trip;
  onCurate: () => void;
  /** Called when a map pin is dragged + dropped onto the Scouted area.
   *  Parent looks up the item by id (from the live search results) and
   *  calls addPlace. Desktop-only — touch DnD isn't supported. */
  onPlaceDropped?: (itemId: string) => void;
}

/** Time-of-day a TripPlace anchors to on `day` in the timeline.
 * Mirrors TripPanel's `timelineTimeFor` — activities use scheduledTime,
 * lodging uses its check-in / check-out boundary time. */
const timelineTimeFor = (p: TripPlace, day: number): string | null => {
  if (p.placeType === 'activity') return p.scheduledTime ?? null;
  if (p.placeType === 'lodging') return lodgingTimelineTime(p, day);
  return null;
};

/** Build the day's chronologically-sorted timeline rows. Activities on
 * `day` + any lodging whose check-in / check-out lands on `day`. */
const buildDayTimeline = (places: Array<TripPlace>, day: number): Array<TripPlace> => {
  const rows: Array<TripPlace> = [];
  for (const p of places) {
    if (p.placeType === 'activity' && p.scheduledDay === day) {
      rows.push(p);
      continue;
    }
    if (p.placeType === 'lodging' && timelineTimeFor(p, day) != null) {
      rows.push(p);
    }
  }
  return rows.sort((a, b) => {
    const aTime = timelineTimeFor(a, day);
    const bTime = timelineTimeFor(b, day);
    if (!(aTime || bTime)) return 0;
    if (!aTime) return 1;
    if (!bTime) return -1;
    return aTime.localeCompare(bTime);
  });
};

const parseHHMMToMinutes = (time: string): number | null => {
  const [h, m] = time.split(':').map(Number);
  if (h == null || Number.isNaN(h) || m == null || Number.isNaN(m)) return null;
  return h * 60 + m;
};

/** Unscheduled minutes between two consecutive rows. Lodging boundaries
 * are zero-duration; activities without `estimatedDurationMinutes` are
 * treated as zero too (so we always show start-to-start span as free
 * time). Returns null when either row lacks an anchored time. */
const timelineGapMinutes = (prev: TripPlace, next: TripPlace, day: number): number | null => {
  const prevTime = timelineTimeFor(prev, day);
  const nextTime = timelineTimeFor(next, day);
  if (!(prevTime && nextTime)) return null;
  const prevStart = parseHHMMToMinutes(prevTime);
  const nextStart = parseHHMMToMinutes(nextTime);
  if (prevStart == null || nextStart == null) return null;
  const prevDuration = prev.placeType === 'lodging' ? 0 : (prev.estimatedDurationMinutes ?? 0);
  return nextStart - (prevStart + prevDuration);
};

/** Compact day-name line ("Mon · Jun 4 · Day 1") above each day's mini
 * timeline. Mid-dot separators echo the place-card meta line style. */
const DayHeader = ({ trip, day }: { trip: Trip; day: number }) => {
  const iso = dateForDay(trip, day);
  const d = new Date(`${iso}T00:00:00`);
  const weekday = d.toLocaleDateString('en-US', { weekday: 'short' });
  const monthDay = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return (
    <div className="flex items-center gap-1.5 px-1 pt-1.5 pb-1 text-[10px] uppercase tracking-wider font-semibold text-content-subtle">
      <span className="text-content-text">{weekday}</span>
      <span className="text-content-disabled">·</span>
      <span>{monthDay}</span>
      <span className="text-content-disabled">·</span>
      <span>Day {day}</span>
    </div>
  );
};

/** Single compact row in the per-day timeline. Time on the left, pin,
 * name. No actions, no badges — readers tap into Curate mode to edit. */
const TimelineRow = ({
  tripPlace,
  day,
  isSelected,
  onClick,
}: {
  tripPlace: TripPlace;
  day: number;
  isSelected: boolean;
  onClick: () => void;
}) => {
  const def = categoryDefinitions[tripPlace.item.appCategory];
  const time = timelineTimeFor(tripPlace, day);
  return (
    <button
      type="button"
      onClick={onClick}
      className={twx(
        'w-full flex items-center gap-1.5 px-1 py-1 rounded-md text-left transition-colors',
        isSelected ? 'bg-primary-subtle' : 'hover:bg-content-focal',
      )}
    >
      <span
        className={twx(
          // text-left so every time's leftmost glyph sits at the same x
          // as the free-time pill below (left-1). text-right would
          // right-align in the column, making "9am" and "1:30pm" start
          // at different x positions and breaking the vertical
          // alignment with the gap pills.
          'shrink-0 w-9 text-[10px] font-medium tabular-nums text-left',
          isSelected ? 'text-primary-focal' : 'text-content-text',
        )}
      >
        {time ? formatScheduledTime(time) : '—'}
      </span>
      <CategoryPin color={def.pinColor} icon={def.icon} size="xs" solid={true} />
      <span className="flex-1 min-w-0 text-xs text-content-text truncate">
        {tripPlace.item.name}
      </span>
    </button>
  );
};

/** Compact free-time indicator between two timeline rows. Mirrors the
 * Curate-mode gap visual: a thin horizontal line spanning the row
 * (fading at the right edge) with a small pill centered on the line.
 * The line aligns to the pin column so it reads as a "branch" off the
 * timeline geometry. */
const TimelineGap = ({ minutes }: { minutes: number }) => {
  const label = formatDurationMinutes(minutes);
  if (!label) return null;
  return (
    <div className="relative h-5 pointer-events-none">
      {/* Horizontal divider line. Starts at the row's left padding
          (left-1) so it aligns vertically with the time-text column of
          the surrounding rows, and runs to the right edge with a soft
          fade so it dissolves rather than stopping abruptly. */}
      <div
        className="absolute left-1 right-1 top-1/2 -translate-y-1/2 h-px bg-secondary-emphasized/30"
        style={{
          maskImage: 'linear-gradient(to right, black 0%, black 75%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to right, black 0%, black 75%, transparent 100%)',
        }}
      />
      {/* Pill anchored to the left edge — vertically aligned with the
          time text of the surrounding rows. bg-white covers the line
          behind it; the line continues past the pill to the right
          edge, fading out. */}
      <div className="absolute left-1 right-1 top-1/2 -translate-y-1/2 flex justify-start">
        {/* The pill breathes slowly — reinforces the Scout-mode message
            that this is unscheduled time the user might want to fill.
            Subtler than a shimmer; doesn't compete with the row content. */}
        <span className="inline-flex items-center px-1.5 py-px rounded-full text-[9px] leading-none font-medium text-content-subtle bg-white border border-secondary-emphasized/30 whitespace-nowrap motion-safe:animate-kawaii-breath origin-center">
          {label} free
        </span>
      </div>
    </div>
  );
};

const ScoutedListPlaceRow = ({
  tripPlace,
  isSelected,
  onClick,
  onRemove,
  isFreshlyAdded = false,
}: {
  tripPlace: TripPlace;
  isSelected: boolean;
  onClick: () => void;
  onRemove: () => void;
  /** When true, the row uses the dramatic `kawaii-drop-bloom` entrance
   *  (scale-from-95 + accent glow) instead of the routine slide-in.
   *  Set by the parent for places whose `addedAt` is within the last
   *  ~1500ms — celebrates drag-drop + heart-tap additions. */
  isFreshlyAdded?: boolean;
}) => {
  const def = categoryDefinitions[tripPlace.item.appCategory];
  return (
    <div
      className={twx(
        'group flex items-start gap-2 p-2 rounded-lg transition-colors',
        isFreshlyAdded
          ? 'motion-safe:animate-kawaii-drop-bloom'
          : 'motion-safe:animate-kawaii-row-enter',
        isSelected ? 'bg-primary-subtle' : 'hover:bg-content-focal',
      )}
    >
      <button
        type="button"
        onClick={onClick}
        className="flex-1 min-w-0 flex items-start gap-2 text-left"
      >
        <CategoryPin color={def.pinColor} icon={def.icon} size="xs" className="mt-0.5" />
        <span className="flex-1 min-w-0">
          <span
            className={twx(
              'block text-xs font-medium truncate',
              isSelected ? 'text-primary-focal' : 'text-content-text',
            )}
          >
            {tripPlace.item.name}
          </span>
          <span className="block text-[10px] text-content-subtle truncate">{def.label}</span>
        </span>
      </button>
      <IconButton
        icon={faTrashCan}
        ariaLabel={`Remove ${tripPlace.item.name}`}
        title="Remove from trip"
        tone="error"
        shape="circle"
        size="xs"
        onClick={onRemove}
        className="opacity-0 group-hover:opacity-100 transition-opacity"
      />
    </div>
  );
};

const DROP_ZONE_ID = 'scouted';

const ScoutedListImpl = ({ trip, onCurate, onPlaceDropped }: ScoutedListProps) => {
  const { removePlace } = useTripContext();
  const [selectedItemId, setSelectedItemId] = useQueryState('item', parseAsString);
  const [isDropTarget, setIsDropTarget] = useState(false);

  const unscheduled = trip.places.filter((p) => p.scheduledDay == null);
  const scheduled = trip.places.filter((p) => p.scheduledDay != null);

  // Drag-and-drop runs over window-level CustomEvents dispatched by the
  // marker's pointer-event drag implementation (see ItineraryMap). We
  // listen for `drag-over` to toggle the hover state when the cursor is
  // over our zone, and `dropped` to actually add the place. The HTML5
  // DnD path was abandoned because Mapbox's capture-phase pointerdown
  // handler calls preventDefault and kills the browser's drag init.
  const dropEnabled = onPlaceDropped != null;
  useEffect(() => {
    if (!dropEnabled) return;
    const onDragOver = (e: Event) => {
      const detail = (e as CustomEvent<{ dropZoneId: string | null }>).detail;
      setIsDropTarget(detail.dropZoneId === DROP_ZONE_ID);
    };
    const onDropped = (e: Event) => {
      const detail = (e as CustomEvent<{ itemId: string; dropZoneId: string }>).detail;
      if (detail.dropZoneId === DROP_ZONE_ID) onPlaceDropped?.(detail.itemId);
    };
    const onDragEnd = () => setIsDropTarget(false);
    window.addEventListener('trippy-place-drag-over', onDragOver);
    window.addEventListener('trippy-place-dropped', onDropped);
    window.addEventListener('trippy-place-drag-end', onDragEnd);
    return () => {
      window.removeEventListener('trippy-place-drag-over', onDragOver);
      window.removeEventListener('trippy-place-dropped', onDropped);
      window.removeEventListener('trippy-place-drag-end', onDragEnd);
    };
  }, [dropEnabled, onPlaceDropped]);

  return (
    <aside className="w-full md:w-[280px] shrink-0 h-full border-l border-content-divider bg-white flex flex-col">
      <div className="shrink-0 px-3 py-3 border-b border-content-divider bg-primary-subtle flex items-start gap-2">
        {/* Text column — small bold name, meta items on separate lines.
            Same styling as the selected-trip row in the trip selector
            menu so the two surfaces stay visually consistent. */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-primary-focal truncate">{trip.name}</p>
          <p className="text-xs text-content-subtle mt-0.5 truncate flex items-center gap-1">
            <FontAwesomeIcon
              icon={faCalendar}
              style={{ width: 9, height: 9 }}
              className="shrink-0"
            />
            <span className="truncate">
              {formatDateRange(trip.startDate, trip.endDate)}
              <span className="mx-1 text-base leading-none">·</span>
              {trip.places.length} {trip.places.length === 1 ? 'place' : 'places'}
            </span>
          </p>
          {trip.location && (
            <p className="text-xs text-content-subtle truncate flex items-center gap-1">
              <FontAwesomeIcon
                icon={faLocationDot}
                style={{ width: 9, height: 9 }}
                className="shrink-0"
              />
              {formatAddress(trip.location)}
            </p>
          )}
        </div>
        {/* Trip-photos corner — pinned top-right, vertically aligned
            with the trip name via items-start on the parent flex row. */}
        <TripPhotosCorner trip={trip} canEdit={true} />
      </div>

      <div className="flex-1 overflow-y-auto px-2 py-2">
        {/* Scouted section. Doubles as a drop target on desktop: the
            user can drag a pin from the map and drop it here to add the
            place to the trip's Scouted bucket. The `data-drop-zone`
            attribute is what the marker's pointer-event drag hit-tests
            against via `document.elementFromPoint` + `closest()`. */}
        <div
          data-drop-zone={dropEnabled ? DROP_ZONE_ID : undefined}
          className={twx(
            'rounded-lg transition-colors',
            isDropTarget
              ? 'bg-secondary-subtle ring-2 ring-secondary-emphasized ring-offset-2 ring-offset-white'
              : '',
          )}
        >
          <div className="flex items-center justify-between px-1 py-0.5 mb-1">
            <SectionEyebrow as="span">Scouted</SectionEyebrow>
            <span className="text-[10px] text-content-disabled">{unscheduled.length}</span>
          </div>
          {/* Drop-hover feedback is the ring + bg on the wrapping div
              ONLY. Earlier versions swapped the empty-state for a "drop
              here" message and appended an extra row in the populated
              case — both changed the drop-zone's height, which moved
              it out from under the cursor, which flipped isDropTarget
              false, which restored the original height, which put the
              cursor back over it… an infinite layout-driven flicker.
              The ring + tinted bg alone gives clear "drop here" feedback
              without touching the size. */}
          {unscheduled.length === 0 ? (
            <InlineEmptyState
              size="sm"
              icon={faMagnifyingGlassLocation}
              title="Nothing scouted yet"
              message={
                dropEnabled
                  ? 'Tap the heart on a place — or drag a pin from the map.'
                  : 'Tap the heart on a place to add it to your scouted list.'
              }
            />
          ) : (
            <div className="space-y-0.5">
              {unscheduled.map((tp) => (
                <ScoutedListPlaceRow
                  key={tp.item.id}
                  tripPlace={tp}
                  isSelected={selectedItemId === tp.item.id}
                  onClick={() => void setSelectedItemId(tp.item.id)}
                  onRemove={() => removePlace(trip.id, tp.item.id)}
                  isFreshlyAdded={Date.now() - tp.addedAt < 1500}
                />
              ))}
            </div>
          )}
        </div>

        {scheduled.length > 0 && (
          <>
            <div className="flex items-center justify-between px-1 py-0.5 mt-4 mb-1">
              <SectionEyebrow as="span">Curated</SectionEyebrow>
              <span className="text-[10px] text-content-disabled">{scheduled.length}</span>
            </div>
            {/* Per-day timeline. All trip days are rendered stacked
                (skipping days with nothing scheduled), so the user can
                see the whole schedule at-a-glance and spot gaps worth
                filling with more scouting. */}
            <div className="space-y-1">
              {Array.from({ length: tripDayCount(trip) }, (_, i) => i + 1).map((day) => {
                const dayRows = buildDayTimeline(scheduled, day);
                if (dayRows.length === 0) return null;
                return (
                  <div
                    key={day}
                    className="border border-content-divider rounded-lg overflow-hidden"
                  >
                    <DayHeader trip={trip} day={day} />
                    <div className="px-1 pb-1">
                      {dayRows.map((tp, rowIdx) => {
                        const next = dayRows[rowIdx + 1];
                        const gap = next ? timelineGapMinutes(tp, next, day) : null;
                        return (
                          <Fragment key={tp.item.id}>
                            <TimelineRow
                              tripPlace={tp}
                              day={day}
                              isSelected={selectedItemId === tp.item.id}
                              onClick={() => void setSelectedItemId(tp.item.id)}
                            />
                            {gap != null && gap > 0 && <TimelineGap minutes={gap} />}
                          </Fragment>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {/* Places scheduled to a day but missing their anchor time
                  (activity without scheduledTime, lodging without
                  checkInTime/checkOutTime) don't surface in any day
                  block above. Surface them in an "Unscheduled time"
                  bucket so they're not silently lost. */}
              {(() => {
                const orphans = scheduled.filter((p) => {
                  if (p.scheduledDay == null) return false;
                  const day = p.scheduledDay;
                  return timelineTimeFor(p, day) == null;
                });
                if (orphans.length === 0) return null;
                return (
                  <div className="border border-content-divider rounded-lg overflow-hidden">
                    <div className="px-1 pt-1.5 pb-1 text-[10px] uppercase tracking-wider font-semibold text-content-subtle">
                      No time set
                    </div>
                    <div className="px-1 pb-1 space-y-0.5">
                      {orphans.map((tp) => (
                        <ScoutedListPlaceRow
                          key={tp.item.id}
                          tripPlace={tp}
                          isSelected={selectedItemId === tp.item.id}
                          onClick={() => void setSelectedItemId(tp.item.id)}
                          onRemove={() => removePlace(trip.id, tp.item.id)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </>
        )}
      </div>

      <div className="shrink-0 p-2 border-t border-content-divider">
        <Button size="sm" fullWidth icon={faCalendarHeart} onClick={onCurate}>
          Curate
        </Button>
      </div>
    </aside>
  );
};

// Memoized so unrelated AppInner re-renders don't repaint the Scout sidebar.
export const ScoutedList = memo(ScoutedListImpl);
